// src/components/PrescriptionTestMatcher.tsx
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  LinearProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  LocalHospital as HospitalIcon,
  Science as ScienceIcon,
  PriceCheck as PriceIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { ParsedTest, MatchedTestDetails } from '../../types/test.types';

// Styled components for luxury look
const LuxuryCard = styled(Card)({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  borderRadius: '16px',
  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-50%',
    width: '200%',
    height: '200%',
    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
    transform: 'rotate(30deg)',
  }
});

const GoldChip = styled(Chip)({
  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
  color: '#000',
  fontWeight: 'bold',
  '& .MuiChip-label': {
    fontWeight: 600,
  },
});

// Styled components for the accordion with better contrast
const StyledAccordion = styled(Accordion)({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  borderRadius: '12px !important',
  marginBottom: '16px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  '& .MuiAccordionSummary-root': {
    color: '#1a1a2e',
    fontWeight: 600,
  },
  '& .MuiAccordionDetails-root': {
    color: '#333',
  },
});

const StyledTableCell = styled(TableCell)({
  fontWeight: 500,
  '&.header': {
    fontWeight: 700,
    backgroundColor: '#f5f5f5',
  },
});

interface PrescriptionTestMatcherProps {
  extractedTests: ParsedTest[];
  unmatchedTests?: string[];
  onTestSelect?: (test: MatchedTestDetails) => void;
  selectedTests?: number[];
  onConfirmSelection?: () => void;
}

export const PrescriptionTestMatcher: React.FC<PrescriptionTestMatcherProps> = ({
  extractedTests,
  unmatchedTests = [],
  onTestSelect,
  selectedTests = [],
  onConfirmSelection,
}) => {
  const [expanded, setExpanded] = useState<string | false>('panel1');

  const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const getConfidenceColor = (confidence: number = 0) => {
    if (confidence >= 0.9) return '#4caf50';
    if (confidence >= 0.7) return '#ff9800';
    return '#f44336';
  };

  const getConfidenceIcon = (confidence: number = 0) => {
    if (confidence >= 0.9) return <CheckIcon sx={{ color: '#4caf50' }} />;
    if (confidence >= 0.7) return <WarningIcon sx={{ color: '#ff9800' }} />;
    return <ErrorIcon sx={{ color: '#f44336' }} />;
  };

  // Filter tests that have matchedTest defined
  const matchedTests = extractedTests.filter(test => test.matchedTest);
  
  const totalAmount = matchedTests
    .filter(test => selectedTests.includes(test.matchedTest!.TestId))
    .reduce((sum, test) => sum + (test.matchedTest?.Amount || 0), 0);

  return (
    <Box sx={{ mt: 3 }}>
      {/* Summary Card */}
      <LuxuryCard sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box display="flex" alignItems="center" gap={2}>
                <HospitalIcon sx={{ fontSize: 48, opacity: 0.9 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 300, opacity: 0.9 }}>
                    Test Analysis Summary
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {matchedTests.length} Tests Detected
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box display="flex" justifyContent="flex-end" gap={3}>
                <Box textAlign="center">
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#FFD700' }}>
                    {selectedTests.length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Selected
                  </Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#FFD700' }}>
                    ₹{totalAmount}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Total Amount
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </LuxuryCard>

      {/* Main Content */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Matched Tests */}
          <StyledAccordion 
            expanded={expanded === 'panel1'} 
            onChange={handleAccordionChange('panel1')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={2}>
                <ScienceIcon sx={{ color: '#667eea' }} />
                <Typography variant="h6">Matched Tests</Typography>
                <GoldChip label={`${matchedTests.length} found`} size="small" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {matchedTests.length === 0 ? (
                <Typography sx={{ color: '#666', textAlign: 'center', py: 3 }}>
                  No matched tests found
                </Typography>
              ) : (
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell className="header">Status</StyledTableCell>
                        <StyledTableCell className="header">Extracted Test</StyledTableCell>
                        <StyledTableCell className="header">Matched Test</StyledTableCell>
                        <StyledTableCell className="header" align="center">Confidence</StyledTableCell>
                        <StyledTableCell className="header" align="right">Amount</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {matchedTests.map((test, index) => (
                        <TableRow 
                          key={index}
                          hover
                          sx={{ 
                            cursor: 'pointer',
                            backgroundColor: selectedTests.includes(test.matchedTest!.TestId) 
                              ? 'rgba(102, 126, 234, 0.1)' 
                              : 'inherit',
                            '&:hover': {
                              backgroundColor: 'rgba(102, 126, 234, 0.05)',
                            },
                          }}
                          onClick={() => test.matchedTest && onTestSelect?.(test.matchedTest)}
                        >
                          <StyledTableCell>
                            <IconButton size="small" sx={{ p: 0 }}>
                              {selectedTests.includes(test.matchedTest!.TestId) ? (
                                <CheckIcon sx={{ color: '#4caf50' }} />
                              ) : (
                                <CheckIcon sx={{ color: '#ccc' }} />
                              )}
                            </IconButton>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {test.originalName || test.name}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {test.matchedTest?.TestName}
                              </Typography>
                              {test.matchedTest?.TestCode && (
                                <Typography variant="caption" sx={{ color: '#666' }}>
                                  Code: {test.matchedTest.TestCode}
                                </Typography>
                              )}
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            <Tooltip title={`${((test.confidence || 0) * 100).toFixed(0)}% match`}>
                              <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                {getConfidenceIcon(test.confidence || 0)}
                                <LinearProgress 
                                  variant="determinate" 
                                  value={(test.confidence || 0) * 100}
                                  sx={{ 
                                    width: 60,
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: '#e0e0e0',
                                    '& .MuiLinearProgress-bar': {
                                      bgcolor: getConfidenceColor(test.confidence || 0),
                                    },
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                              ₹{test.matchedTest?.Amount || 0}
                            </Typography>
                          </StyledTableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </AccordionDetails>
          </StyledAccordion>

          {/* Unmatched Tests */}
          {unmatchedTests.length > 0 && (
            <StyledAccordion 
              expanded={expanded === 'panel2'} 
              onChange={handleAccordionChange('panel2')}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={2}>
                  <WarningIcon sx={{ color: '#ff9800' }} />
                  <Typography variant="h6">Unmatched Tests</Typography>
                  <Chip 
                    label={`${unmatchedTests.length} needs review`} 
                    size="small"
                    sx={{ bgcolor: '#ff9800', color: '#fff' }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  These tests were detected in the prescription but don't match our database. 
                  Please review and add if necessary.
                </Alert>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {unmatchedTests.map((test, index) => (
                    <Chip
                      key={index}
                      label={test}
                      variant="outlined"
                      sx={{ 
                        borderColor: '#ff9800',
                        color: '#ff9800',
                        '&:hover': {
                          bgcolor: 'rgba(255,152,0,0.1)',
                        },
                      }}
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </StyledAccordion>
          )}
        </Grid>

        {/* Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper 
            sx={{ 
              p: 3,
              background: 'linear-gradient(135deg, #141E30 0%, #243B55 100%)',
              borderRadius: '16px',
              position: 'sticky',
              top: 24,
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
              <PriceIcon sx={{ color: '#FFD700' }} />
              Selected Tests Summary
            </Typography>
            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
            
            {selectedTests.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', py: 3 }}>
                No tests selected yet
              </Typography>
            ) : (
              <>
                {matchedTests
                  .filter(test => selectedTests.includes(test.matchedTest!.TestId))
                  .map((test, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'white' }}>
                          {test.matchedTest!.TestName}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
                          ₹{test.matchedTest!.Amount || 0}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        {test.matchedTest!.TestCode}
                      </Typography>
                    </Box>
                  ))}
                
                <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
                
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" sx={{ color: 'white' }}>Total</Typography>
                  <Typography variant="h5" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
                    ₹{totalAmount}
                  </Typography>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={onConfirmSelection}
                  sx={{ 
                    mt: 3,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #764ba2 0%, #667ba2 100%)',
                    }
                  }}
                >
                  Confirm & Continue
                </Button>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

// // src/components/PrescriptionTestMatcher.tsx
// import React, { useState } from 'react';
// import {
//   Box,
//   Card,
//   CardContent,
//   Typography,
//   Grid,
//   Chip,
//   Button,
//   Divider,
//   LinearProgress,
//   Alert,
//   Accordion,
//   AccordionSummary,
//   AccordionDetails,
//   Tooltip,
//   IconButton,
//   Paper,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
// } from '@mui/material';
// import {
//   CheckCircle as CheckIcon,
//   Error as ErrorIcon,
//   Warning as WarningIcon,
//   ExpandMore as ExpandMoreIcon,
//   LocalHospital as HospitalIcon,
//   Science as ScienceIcon,
//   PriceCheck as PriceIcon,
// } from '@mui/icons-material';
// import { styled } from '@mui/material/styles';
// import { ParsedTest, MatchedTestDetails } from '../types/test.types';

// // Styled components for luxury look - remove unused theme parameter
// const LuxaryCard = styled(Card)({
//   background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
//   color: 'white',
//   borderRadius: '16px',
//   boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
//   position: 'relative',
//   overflow: 'hidden',
//   '&::before': {
//     content: '""',
//     position: 'absolute',
//     top: '-50%',
//     right: '-50%',
//     width: '200%',
//     height: '200%',
//     background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
//     transform: 'rotate(30deg)',
//   }
// });

// const GoldChip = styled(Chip)({
//   background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
//   color: '#000',
//   fontWeight: 'bold',
//   '& .MuiChip-label': {
//     fontWeight: 600,
//   },
// });

// interface PrescriptionTestMatcherProps {
//   extractedTests: ParsedTest[];
//   unmatchedTests?: string[];
//   onTestSelect?: (test: MatchedTestDetails) => void;
//   selectedTests?: number[];
//   onConfirmSelection?: () => void;
// }

// export const PrescriptionTestMatcher: React.FC<PrescriptionTestMatcherProps> = ({
//   extractedTests,
//   unmatchedTests = [],
//   onTestSelect,
//   selectedTests = [],
//   onConfirmSelection,
// }) => {
//   const [expanded, setExpanded] = useState<string | false>('panel1');

//   // Remove unused event parameter with underscore prefix
//   const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
//     setExpanded(isExpanded ? panel : false);
//   };

//   const getConfidenceColor = (confidence: number = 0) => {
//     if (confidence >= 0.9) return '#4caf50';
//     if (confidence >= 0.7) return '#ff9800';
//     return '#f44336';
//   };

//   const getConfidenceIcon = (confidence: number = 0) => {
//     if (confidence >= 0.9) return <CheckIcon sx={{ color: '#4caf50' }} />;
//     if (confidence >= 0.7) return <WarningIcon sx={{ color: '#ff9800' }} />;
//     return <ErrorIcon sx={{ color: '#f44336' }} />;
//   };

//   // Filter tests that have matchedTest defined
//   const matchedTests = extractedTests.filter(test => test.matchedTest);
  
//   const totalAmount = matchedTests
//     .filter(test => selectedTests.includes(test.matchedTest!.TestId))
//     .reduce((sum, test) => sum + (test.matchedTest?.Amount || 0), 0);

//   return (
//     <Box sx={{ mt: 3 }}>
//       {/* Summary Card */}
//       <LuxaryCard sx={{ mb: 3 }}>
//         <CardContent>
//           <Grid container spacing={3} alignItems="center">
//             {/* Using size prop for MUI v7 */}
//             <Grid size={{ xs: 12, md: 6 }}>
//               <Box display="flex" alignItems="center" gap={2}>
//                 <HospitalIcon sx={{ fontSize: 48, opacity: 0.9 }} />
//                 <Box>
//                   <Typography variant="h6" sx={{ fontWeight: 300, opacity: 0.9 }}>
//                     Test Analysis Summary
//                   </Typography>
//                   <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
//                     {matchedTests.length} Tests Detected
//                   </Typography>
//                 </Box>
//               </Box>
//             </Grid>
//             <Grid size={{ xs: 12, md: 6 }}>
//               <Box display="flex" justifyContent="flex-end" gap={3}>
//                 <Box textAlign="center">
//                   <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#FFD700' }}>
//                     {selectedTests.length}
//                   </Typography>
//                   <Typography variant="body2" sx={{ opacity: 0.8 }}>
//                     Selected
//                   </Typography>
//                 </Box>
//                 <Box textAlign="center">
//                   <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#FFD700' }}>
//                     ₹{totalAmount}
//                   </Typography>
//                   <Typography variant="body2" sx={{ opacity: 0.8 }}>
//                     Total Amount
//                   </Typography>
//                 </Box>
//               </Box>
//             </Grid>
//           </Grid>
//         </CardContent>
//       </LuxaryCard>

//       {/* Main Content */}
//       <Grid container spacing={3}>
//         <Grid size={{ xs: 12, md: 8 }}>
//           {/* Matched Tests */}
//           <Accordion 
//             expanded={expanded === 'panel1'} 
//             onChange={handleAccordionChange('panel1')}
//             sx={{ 
//               background: 'rgba(255,255,255,0.05)',
//               backdropFilter: 'blur(10px)',
//               borderRadius: '12px !important',
//               mb: 2,
//               '& .MuiAccordionSummary-root': {
//                 color: 'white',
//               },
//               '& .MuiAccordionDetails-root': {
//                 color: 'white',
//               },
//             }}
//           >
//             <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
//               <Box display="flex" alignItems="center" gap={2}>
//                 <ScienceIcon sx={{ color: '#667eea' }} />
//                 <Typography variant="h6">Matched Tests</Typography>
//                 <GoldChip label={`${matchedTests.length} found`} size="small" />
//               </Box>
//             </AccordionSummary>
//             <AccordionDetails>
//               {matchedTests.length === 0 ? (
//                 <Typography sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', py: 3 }}>
//                   No matched tests found
//                 </Typography>
//               ) : (
//                 <TableContainer component={Paper} sx={{ background: 'transparent' }}>
//                   <Table>
//                     <TableHead>
//                       <TableRow>
//                         <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>Status</TableCell>
//                         <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>Extracted Test</TableCell>
//                         <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>Matched Test</TableCell>
//                         <TableCell align="center" sx={{ color: 'rgba(255,255,255,0.7)' }}>Confidence</TableCell>
//                         <TableCell align="right" sx={{ color: 'rgba(255,255,255,0.7)' }}>Amount</TableCell>
//                       </TableRow>
//                     </TableHead>
//                     <TableBody>
//                       {matchedTests.map((test, index) => (
//                         <TableRow 
//                           key={index}
//                           hover
//                           sx={{ 
//                             cursor: 'pointer',
//                             bgcolor: selectedTests.includes(test.matchedTest!.TestId) 
//                               ? 'rgba(102, 126, 234, 0.2)' 
//                               : 'transparent',
//                             '&:hover': {
//                               bgcolor: 'rgba(102, 126, 234, 0.1)',
//                             },
//                           }}
//                           onClick={() => test.matchedTest && onTestSelect?.(test.matchedTest)}
//                         >
//                           <TableCell>
//                             <IconButton size="small">
//                               {selectedTests.includes(test.matchedTest!.TestId) ? (
//                                 <CheckIcon sx={{ color: '#4caf50' }} />
//                               ) : (
//                                 <CheckIcon sx={{ color: 'rgba(255,255,255,0.3)' }} />
//                               )}
//                             </IconButton>
//                           </TableCell>
//                           <TableCell>
//                             <Typography variant="body2" sx={{ fontWeight: 500, color: 'white' }}>
//                               {test.originalName || test.name}
//                             </Typography>
//                           </TableCell>
//                           <TableCell>
//                             <Box>
//                               <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'white' }}>
//                                 {test.matchedTest?.TestName}
//                               </Typography>
//                               {test.matchedTest?.TestCode && (
//                                 <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
//                                   Code: {test.matchedTest.TestCode}
//                                 </Typography>
//                               )}
//                             </Box>
//                           </TableCell>
//                           <TableCell align="center">
//                             <Tooltip title={`${((test.confidence || 0) * 100).toFixed(0)}% match`}>
//                               <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
//                                 {getConfidenceIcon(test.confidence || 0)}
//                                 <LinearProgress 
//                                   variant="determinate" 
//                                   value={(test.confidence || 0) * 100}
//                                   sx={{ 
//                                     width: 60,
//                                     height: 6,
//                                     borderRadius: 3,
//                                     bgcolor: 'rgba(255,255,255,0.1)',
//                                     '& .MuiLinearProgress-bar': {
//                                       bgcolor: getConfidenceColor(test.confidence || 0),
//                                     },
//                                   }}
//                                 />
//                               </Box>
//                             </Tooltip>
//                           </TableCell>
//                           <TableCell align="right">
//                             <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#FFD700' }}>
//                               ₹{test.matchedTest?.Amount || 0}
//                             </Typography>
//                           </TableCell>
//                         </TableRow>
//                       ))}
//                     </TableBody>
//                   </Table>
//                 </TableContainer>
//               )}
//             </AccordionDetails>
//           </Accordion>

//           {/* Unmatched Tests */}
//           {unmatchedTests.length > 0 && (
//             <Accordion 
//               expanded={expanded === 'panel2'} 
//               onChange={handleAccordionChange('panel2')}
//               sx={{ 
//                 background: 'rgba(255,255,255,0.05)',
//                 backdropFilter: 'blur(10px)',
//                 borderRadius: '12px !important',
//                 '& .MuiAccordionSummary-root': {
//                   color: 'white',
//                 },
//                 '& .MuiAccordionDetails-root': {
//                   color: 'white',
//                 },
//               }}
//             >
//               <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
//                 <Box display="flex" alignItems="center" gap={2}>
//                   <WarningIcon sx={{ color: '#ff9800' }} />
//                   <Typography variant="h6">Unmatched Tests</Typography>
//                   <Chip 
//                     label={`${unmatchedTests.length} needs review`} 
//                     size="small"
//                     sx={{ bgcolor: '#ff9800', color: '#000' }}
//                   />
//                 </Box>
//               </AccordionSummary>
//               <AccordionDetails>
//                 <Alert severity="warning" sx={{ mb: 2 }}>
//                   These tests were detected in the prescription but don't match our database. 
//                   Please review and add if necessary.
//                 </Alert>
//                 <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
//                   {unmatchedTests.map((test, index) => (
//                     <Chip
//                       key={index}
//                       label={test}
//                       variant="outlined"
//                       sx={{ 
//                         borderColor: '#ff9800',
//                         color: '#ff9800',
//                         '&:hover': {
//                           bgcolor: 'rgba(255,152,0,0.1)',
//                         },
//                       }}
//                     />
//                   ))}
//                 </Box>
//               </AccordionDetails>
//             </Accordion>
//           )}
//         </Grid>

//         {/* Sidebar */}
//         <Grid size={{ xs: 12, md: 4 }}>
//           <Paper 
//             sx={{ 
//               p: 3,
//               background: 'linear-gradient(135deg, #141E30 0%, #243B55 100%)',
//               borderRadius: '16px',
//               position: 'sticky',
//               top: 24,
//             }}
//           >
//             <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
//               <PriceIcon sx={{ color: '#FFD700' }} />
//               Selected Tests Summary
//             </Typography>
//             <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
            
//             {selectedTests.length === 0 ? (
//               <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', py: 3 }}>
//                 No tests selected yet
//               </Typography>
//             ) : (
//               <>
//                 {matchedTests
//                   .filter(test => selectedTests.includes(test.matchedTest!.TestId))
//                   .map((test, index) => (
//                     <Box key={index} sx={{ mb: 2 }}>
//                       <Box display="flex" justifyContent="space-between" alignItems="center">
//                         <Typography variant="body2" sx={{ fontWeight: 500, color: 'white' }}>
//                           {test.matchedTest!.TestName}
//                         </Typography>
//                         <Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
//                           ₹{test.matchedTest!.Amount || 0}
//                         </Typography>
//                       </Box>
//                       <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
//                         {test.matchedTest!.TestCode}
//                       </Typography>
//                     </Box>
//                   ))}
                
//                 <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
                
//                 <Box display="flex" justifyContent="space-between" alignItems="center">
//                   <Typography variant="h6" sx={{ color: 'white' }}>Total</Typography>
//                   <Typography variant="h5" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
//                     ₹{totalAmount}
//                   </Typography>
//                 </Box>

//                 <Button
//                   fullWidth
//                   variant="contained"
//                   size="large"
//                   onClick={onConfirmSelection}
//                   sx={{ 
//                     mt: 3,
//                     background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
//                     color: 'white',
//                     fontWeight: 600,
//                     '&:hover': {
//                       background: 'linear-gradient(135deg, #764ba2 0%, #667ba2 100%)',
//                     }
//                   }}
//                 >
//                   Confirm & Continue
//                 </Button>
//               </>
//             )}
//           </Paper>
//         </Grid>
//       </Grid>
//     </Box>
//   );
// };